/**
 * Helpers de timezone (Sprint 10.2e).
 *
 * Modelo:
 *   - El Guru configura su disponibilidad en SU timezone (ej. America/Mexico_City).
 *     Cada bloque en guru_availability tiene su columna `timezone`.
 *   - El cliente al reservar ve los slots en SU timezone (default: browser).
 *     El cliente puede cambiar el timezone visible via TimezoneSelector.
 *
 * Conversión:
 *   - Para generar un slot: "9:00 AM en CDMX" → instante UTC via zonedTimeToUtc.
 *   - Para mostrar al cliente: ese UTC → formatInTimeZone(tz_cliente, 'HH:mm').
 *
 * Persistencia:
 *   - El timezone elegido por el cliente se guarda en localStorage como
 *     `gurumba:client_tz` para que próximas visitas lo recuerden.
 */
// date-fns-tz v3 renombró: zonedTimeToUtc → fromZonedTime, utcToZonedTime → toZonedTime
import { fromZonedTime } from 'date-fns-tz';

export interface TimezoneOption {
  value: string;    // IANA tz (ej. "America/Mexico_City")
  label: string;    // Human friendly (ej. "Ciudad de México")
}

/**
 * Timezones soportados — agrupados por región. Cubre LATAM + USA + España.
 * La lista es la "comodidad común" — el browser puede tener cualquier IANA,
 * solo presentamos los que son comunes en nuestro mercado.
 */
export const TIMEZONES: TimezoneOption[] = [
  // LATAM
  { value: 'America/Mexico_City', label: 'Ciudad de México (México)' },
  { value: 'America/Bogota', label: 'Bogotá (Colombia)' },
  { value: 'America/Lima', label: 'Lima (Perú)' },
  { value: 'America/Caracas', label: 'Caracas (Venezuela)' },
  { value: 'America/La_Paz', label: 'La Paz (Bolivia)' },
  { value: 'America/Santiago', label: 'Santiago (Chile)' },
  { value: 'America/Argentina/Buenos_Aires', label: 'Buenos Aires (Argentina)' },
  { value: 'America/Montevideo', label: 'Montevideo (Uruguay)' },
  { value: 'America/Asuncion', label: 'Asunción (Paraguay)' },
  { value: 'America/Guayaquil', label: 'Quito / Guayaquil (Ecuador)' },
  { value: 'America/Panama', label: 'Panamá' },
  { value: 'America/Costa_Rica', label: 'San José (Costa Rica)' },
  { value: 'America/Guatemala', label: 'Guatemala' },
  { value: 'America/Tegucigalpa', label: 'Tegucigalpa (Honduras)' },
  { value: 'America/Managua', label: 'Managua (Nicaragua)' },
  { value: 'America/El_Salvador', label: 'San Salvador (El Salvador)' },
  { value: 'America/Santo_Domingo', label: 'Santo Domingo (Rep. Dominicana)' },
  { value: 'America/Havana', label: 'La Habana (Cuba)' },
  { value: 'America/Puerto_Rico', label: 'San Juan (Puerto Rico)' },
  { value: 'America/Sao_Paulo', label: 'São Paulo (Brasil)' },
  { value: 'America/Port-au-Prince', label: 'Puerto Príncipe (Haití)' },
  // Norteamérica
  { value: 'America/New_York', label: 'Nueva York (EE.UU. Este)' },
  { value: 'America/Chicago', label: 'Chicago (EE.UU. Centro)' },
  { value: 'America/Denver', label: 'Denver (EE.UU. Montaña)' },
  { value: 'America/Los_Angeles', label: 'Los Ángeles (EE.UU. Pacífico)' },
  { value: 'America/Toronto', label: 'Toronto (Canadá Este)' },
  { value: 'America/Vancouver', label: 'Vancouver (Canadá Pacífico)' },
  // Europa
  { value: 'Europe/Madrid', label: 'Madrid (España)' },
];

const LS_KEY = 'gurumba:client_tz';

/**
 * Devuelve el timezone IANA del browser del cliente.
 * Fallback a "America/Mexico_City" si no se puede detectar (raro).
 */
export function getBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'America/Mexico_City';
  } catch {
    return 'America/Mexico_City';
  }
}

/**
 * Devuelve el timezone preferido del cliente:
 *   - Si guardó uno en localStorage, ese.
 *   - Si no, el del browser.
 */
export function getPreferredClientTimezone(): string {
  if (typeof window === 'undefined') return 'America/Mexico_City';
  try {
    const saved = window.localStorage.getItem(LS_KEY);
    if (saved) return saved;
  } catch {
    /* localStorage puede estar deshabilitado */
  }
  return getBrowserTimezone();
}

export function setPreferredClientTimezone(tz: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(LS_KEY, tz);
  } catch {
    /* nada que hacer */
  }
}

/**
 * Crea una fecha (instante UTC) a partir de "hora local en un timezone dado".
 *
 * Ej: getSlotInTimezone(2026-06-26, 9, 0, "America/Mexico_City") devuelve
 * el instante UTC que corresponde a las 9:00 AM en CDMX (= 15:00 UTC).
 *
 * Usar esto para INTERPRETAR las reglas de guru_availability:
 * "el guru dice 9:00 AM" — eso es 9:00 EN SU tz, no en el browser del cliente.
 */
export function createSlotAtZonedTime(
  year: number,
  month: number,        // 0-indexed (igual que Date)
  day: number,
  hours: number,
  minutes: number,
  timezone: string,
): Date {
  // date-fns-tz acepta string ISO o componentes individuales via Date local
  // Truco: construimos un string ISO "2026-06-26T09:00:00" y le decimos en qué tz
  const isoLocal = `${year}-${pad(month + 1)}-${pad(day)}T${pad(hours)}:${pad(minutes)}:00`;
  return fromZonedTime(isoLocal, timezone);
}

function pad(n: number): string {
  return n.toString().padStart(2, '0');
}

/**
 * Devuelve un offset legible del timezone dado, respecto a UTC.
 * Ej: "America/Mexico_City" en verano → "GMT-5"
 */
export function tzOffsetLabel(timezone: string, date: Date = new Date()): string {
  try {
    // Intl tiene "shortOffset" en navegadores modernos
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'shortOffset',
    }).formatToParts(date);
    const offset = parts.find((p) => p.type === 'timeZoneName')?.value;
    if (offset) return offset; // ej. "GMT-5"
  } catch {
    /* fallback abajo */
  }
  // Fallback manual: calcular offset en horas
  try {
    const tzDate = new Date(date.toLocaleString('en-US', { timeZone: timezone }));
    const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
    const diffHours = Math.round((tzDate.getTime() - utcDate.getTime()) / (60 * 60 * 1000));
    return `GMT${diffHours >= 0 ? '+' : ''}${diffHours}`;
  } catch {
    return '';
  }
}

/**
 * Etiqueta corta para el header: "Buenos Aires (GMT-3)"
 */
export function tzShortLabel(timezone: string): string {
  const cityRaw = timezone.split('/').pop() ?? timezone;
  const city = cityRaw.replace(/_/g, ' ');
  const offset = tzOffsetLabel(timezone);
  return offset ? `${city} (${offset})` : city;
}
